import { useToast } from "@ligo/layout-design-core";
import { Errors } from "classes";
import { dayFormat } from "consts";
import { format, sub } from "date-fns";
import {
  BaseProviderType,
  HSMBatchModel,
  HSMTemplateModel,
  PermissionProviderType,
} from "models";
import { useProfile } from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as HSMService from "services/hsm";
import { addTimeSuffix, aytyFormatError, equalObjects, sortBy } from "utils";

type FilterType = {
  name: string;
  start: string;
  end: string;
  channel: string | number;
  status: 0 | 1 | -1;
};

type HSMContextType = {
  loading: boolean;
  filter: FilterType;
  hasFilter: boolean;
  list?: HSMBatchModel[];
  hsmTemplates?: HSMTemplateModel[];
  activeHSMs?: HSMBatchModel[];
  activeHSMTemplates?: HSMTemplateModel[];
  getHSMs: () => Promise<void>;
  getHSMTemplates: () => Promise<void>;
  onChangeFilter: (filter: keyof FilterType, value: any) => void;
  onResetFilter: () => void;
};

const HSMContext = createContext({} as HSMContextType);

const endDate = new Date();
const startDate = sub(endDate, { days: 1 });

const initialFilter: FilterType = {
  name: "",
  start: format(startDate, dayFormat),
  end: format(endDate, dayFormat),
  channel: "",
  status: 1,
};

const Provider = ({ children }: BaseProviderType) => {
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>({ ...initialFilter });
  const [list, setList] = useState<HSMBatchModel[]>();
  const [hsmTemplates, setHSMTemplates] = useState<HSMTemplateModel[]>();
  const { pIdProject } = useProfile();
  const { error, warning } = useToast();
  const { t } = useTranslation();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const hasFilter = useMemo(
    () => !equalObjects(initialFilter, filter),
    [filter]
  );

  const activeHSMs = useMemo(() => list?.filter((c) => c.IsActive), [list]);

  const activeHSMTemplates = useMemo(
    () => hsmTemplates?.filter((c) => c.IsActive),
    [hsmTemplates]
  );

  const getHSMs = useCallback(async () => {
    setLoading(true);
    if (pIdProject)
      await HSMService.getHSMBatchs({
        pIdProject,
        pNmHsmBatch: filter.name,
        pDtStart: addTimeSuffix(filter.start),
        pDtFinish: addTimeSuffix(filter.end, true),
      })
        .then(({ data }) => {
          if (data.idReturnAPI > 0)
            setList(sortBy(data.hsmBatchList, "IdHsmBatch", true));
          else warning({ description: t(aytyFormatError(data)) });
        })
        .catch(errorsResolver.defaultError);
    setLoading(false);
  }, [pIdProject, filter, errorsResolver, warning, t]);

  const getHSMTemplates = useCallback(async () => {
    setLoading(true);
    if (pIdProject)
      await HSMService.getHSMTemplates({ pIdProject })
        .then(async ({ data }) => {
          if (data.idReturnAPI > 0)
            setHSMTemplates(data.messageOutboundTemplateList);
          else warning({ description: t("errors.failedToFetchList") });
        })
        .catch(errorsResolver.defaultError);
    setLoading(false);
  }, [pIdProject, errorsResolver, warning, t]);

  const onChangeFilter = useCallback((filter: keyof FilterType, value: any) => {
    setFilter((current) => ({ ...current, [filter]: value }));
  }, []);

  const onResetFilter = useCallback(() => {
    setFilter({ ...initialFilter });
    setList(undefined);
  }, []);

  useEffect(() => {
    getHSMTemplates();
  }, [getHSMTemplates]);

  return (
    <HSMContext.Provider
      value={{
        loading,
        filter,
        hasFilter,
        list,
        hsmTemplates,
        activeHSMs,
        activeHSMTemplates,
        onChangeFilter,
        onResetFilter,
        getHSMs,
        getHSMTemplates,
      }}
    >
      {children}
    </HSMContext.Provider>
  );
};

export const HSMProvider = ({
  hasPermission = true,
  ...args
}: PermissionProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useHSM = () => useContext(HSMContext);
